import { ChangeEvent } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { AdmissionStatus, ApplicationStatus } from '@common-types';
import { CoreChip, CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';

import { SCHOOL_RANKING_DATA } from '@constants/applyingSchoolsPlanner';

import { FormState } from '@interfaces/applications';

import ActionButton from '@components/ActionButton';

import EditIcon from '@images/pencil-button.svg?react';

import {
  sxStyles,
  StyledApplication,
  StyledApplicationButtonGroup,
  StyledApplicationScore,
  StyledApplicationScoreNumber,
  StyledApplicationSection,
} from './PlannerSchoolCardApplication.styles';

import { StyledCardDivider, sxStyles as cardSxStyles, StyledCardInfoLine } from '../../PlannerSchoolCard.styles';

interface PlannerSchoolCardApplicationProps {
  memberId?: number;
  formState: FormState;
  schoolName: string;
  isEditChecklist: boolean;
  isEditApplication: boolean;
  setIsEditApplication: (status: boolean) => void;
  onSelectChange: (formField: keyof FormState) => (event: SelectChangeEvent<unknown>) => void;
  onPriorityChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const PlannerSchoolCardApplication = (props: PlannerSchoolCardApplicationProps) => {
  const { formState, isEditChecklist, setIsEditApplication } = props;
  const { deadlineType, applicationType, applicationStatus, admissionStatus, priority, decision } = formState;

  return (
    <StyledApplication>
      <StyledApplicationSection>
        <CoreTypography sx={sxStyles.sectionTitle} variant="h6" component="h5">
          Application
        </CoreTypography>

        {applicationType && (
          <StyledCardInfoLine>
            <CoreTypography variant="subtitle2" component="p">
              Type:
            </CoreTypography>

            <CoreTypography data-testid="applicationType" variant="body2">
              {applicationType}
            </CoreTypography>
          </StyledCardInfoLine>
        )}

        <StyledCardInfoLine>
          <CoreTypography variant="subtitle2" component="p">
            Admission plan:
          </CoreTypography>

          <CoreTypography data-testid="selectedRound" variant="body2">
            {deadlineType || 'Not Selected'}
          </CoreTypography>
        </StyledCardInfoLine>

        {applicationStatus && (
          <StyledCardInfoLine>
            <CoreTypography variant="subtitle2" component="p">
              Status:
            </CoreTypography>

            <CoreChip
              data-testid="applicationStatus"
              sx={cardSxStyles.chip.applicationStatus(applicationStatus as ApplicationStatus)}
              label={applicationStatus}
              variant="outlined"
            />
          </StyledCardInfoLine>
        )}
      </StyledApplicationSection>

      <StyledCardDivider />

      <StyledApplicationSection>
        <CoreTypography sx={sxStyles.sectionTitle} variant="h6" component="h5">
          School ranking
        </CoreTypography>

        {priority && (
          <StyledApplicationScore>
            <StyledApplicationScoreNumber data-testid="priority">{priority}</StyledApplicationScoreNumber>

            <CoreTypography variant="body2">
              {SCHOOL_RANKING_DATA[priority as keyof typeof SCHOOL_RANKING_DATA]}
            </CoreTypography>
          </StyledApplicationScore>
        )}
      </StyledApplicationSection>

      <StyledCardDivider />

      <StyledApplicationSection>
        <CoreTypography sx={sxStyles.sectionTitle} variant="h6" component="h5">
          Decision
        </CoreTypography>

        {admissionStatus && (
          <StyledCardInfoLine>
            <CoreTypography variant="subtitle2" component="p">
              Admission status:
            </CoreTypography>

            <CoreChip
              data-testid="admissionStatus"
              sx={cardSxStyles.chip.admissionStatus(admissionStatus as AdmissionStatus)}
              label={admissionStatus}
              variant="outlined"
            />
          </StyledCardInfoLine>
        )}

        {decision && (
          <StyledCardInfoLine>
            <CoreTypography variant="subtitle2" component="p">
              Student decision:
            </CoreTypography>

            <CoreTypography data-testid="decision" variant="body2">
              {decision}
            </CoreTypography>
          </StyledCardInfoLine>
        )}
      </StyledApplicationSection>

      <StyledApplicationButtonGroup>
        {isEditChecklist ? (
          <CoreTooltip title="Please save Checklist changes">
            <CoreBox sx={sxStyles.editBtnTooltip}>
              <ActionButton
                size="small"
                variant="outlined"
                disabled
                startIcon={<EditIcon data-testid="disabledEditBtn" />}
                onClick={() => void 0}
              >
                Edit
              </ActionButton>
            </CoreBox>
          </CoreTooltip>
        ) : (
          <ActionButton
            size="small"
            variant="outlined"
            data-testid="activeEditBtn"
            onClick={() => setIsEditApplication(true)}
            startIcon={<EditIcon />}
          >
            Edit
          </ActionButton>
        )}
      </StyledApplicationButtonGroup>
    </StyledApplication>
  );
};
